import { CloudSun } from 'lucide-react';
import type { HourlyWeather } from '../../types';
import type { DashboardLocation } from '../../hooks/useDashboardLocation';

// Dashboard section — hourly weather for the user's location (see useDashboardLocation — derived
// automatically from the profile address, or the first of a carrier's enabled Job Alert areas),
// sourced from the Met Office Weather DataHub's hourly forecast via the backend's
// WeatherController. Purely presentational, mirroring TrafficNewsSection — data is fetched once
// in UserDashboardPage.tsx.
export function WeatherSection({
  location, hours, isLoading, error,
}: {
  location: DashboardLocation;
  hours: HourlyWeather[];
  isLoading: boolean;
  error: unknown;
}) {
  return (
    <div data-testid="dashboard-weather-section" className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
      <div className="px-5 py-3.5 flex items-center gap-2 border-b border-gray-100">
        <CloudSun className="w-4 h-4 text-gray-700" />
        <h2 className="font-bold text-sm text-gray-900">Local Weather</h2>
      </div>

      <div className="p-4">
        {!location.loading && !location.weatherCoords && (
          <p className="text-sm text-gray-400">Add an address to your profile to see the hourly forecast.</p>
        )}

        {location.weatherCoords && (location.loading || isLoading) && (
          <p className="text-sm text-gray-400">Loading forecast…</p>
        )}

        {location.weatherCoords && !location.loading && !isLoading && !!error && (
          <p data-testid="dashboard-weather-error" className="text-sm text-red-500">
            Couldn't load the forecast. Refresh to try again.
          </p>
        )}

        {location.weatherCoords && !location.loading && !isLoading && !error && hours.length === 0 && (
          <p className="text-sm text-gray-400">No forecast available right now.</p>
        )}

        {location.weatherCoords && !location.loading && !isLoading && !error && hours.length > 0 && (
          <div data-testid="dashboard-weather-hourly-scroll" className="flex gap-3 overflow-x-auto pb-1">
            {hours.slice(0, 12).map((hour, i) => (
              <div key={hour.time} data-testid={`dashboard-weather-hour-${i}`} className="flex flex-col items-center w-14 shrink-0">
                <span className="text-xs text-gray-500">
                  {new Date(hour.time).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false })}
                </span>
                <span className="text-2xl my-1">{hour.icon}</span>
                <span className="text-sm font-bold text-gray-900">{hour.temperatureC}°</span>
                <span className="text-xs text-blue-400 mt-0.5">{hour.precipitationProbability}%</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
